// 行事曆的 App 內通知橫幅（網頁開著時；09 §7.2、§9.9；資料 = GET /v3/me/panel-notices，狀態與輪詢見 panelCalNotice.ts；掛在 AppShell）。
//   - C4 沒確認：［阿嬤說吃過了］（代確認）［打給面板］［看這件］；待核准時按的那種沒有代確認，主人多［核准這個網路］
//     （雲端給了 approve_mode → 直接打 location:approve，09 §9.10；備援推算的沒有 → 到面板頁網路卡）。
//   - C3 語音加 / 改、ask_family、摘要：［看這件］/［看行事曆］。ask_family 點開直接開那件的編輯畫面、預填長輩說的新時間。
//   - 每則都能［收起］；一次最多 3 則，其餘「還有 N 則」展開。
//   - D2 手機確認（10 §10）：帳號加了確認手機 →［看我的手機］（帳號頁 V-11）；有人一直要求手機確認 / 驗證方式改了 →［看面板］。
//   - B3 新地點待核准（05 V-09）：［核准這個網路］展開 V-09 選項（允許這次 / 允許 N 小時 → 問門鈴 / 設為常用地點 / 不是，移除 → 再確認；
//     網頁不用解鎖）、［看常用地點］→ 面板頁；PanelPendingBadge = 裝置清單 / 面板頁「待核准」徽章。
import { useEffect, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { panelCall } from './panelCall';
import { isConfirmNotice, noticeAckLabel, noticeBodyFallback, noticeTarget, noticeText, panelCalNotice, visibleNotices, type CalNotice } from './panelCalNotice';
import type { LocationMode } from './panels';
import './panelCalNotice.css';

const SHOW_MAX = 3;
const HOURS = [1, 4, 8, 24];

/** 「待核准」徽章（B3）：這台面板還有沒處理的新地點那則 */
export function PanelPendingBadge({ panelId }: { panelId: string }) {
  const { t } = useTranslation();
  const st = useSyncExternalStore(panelCalNotice.subscribe, panelCalNotice.get);
  if (!st.pendingPanels.has(panelId)) return null;
  return <span className="dv-badge dv-badge-pending" data-testid="panel-pending-badge">{t('panelCalNotice.locationBadge')}</span>;
}

/** 新地點［核准這個網路］的選項（V-09；同 PanelNetworksCard） */
function LocationChooser({ n, busy, onClose }: { n: CalNotice; busy: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [hours, setHours] = useState(4);
  const [askRing, setAskRing] = useState<{ mode: LocationMode; hours?: number } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const modes = n.approveModes ?? [];
  const go = (mode: LocationMode, h?: number, ring?: boolean) => void panelCalNotice.approveLocation(n, mode, h, ring);
  if (askRing) {
    return (
      <span className="cn-loc" data-testid="panel-loc-ring">
        <span>{t('panels.loc.ringAsk')}</span>
        <button className="btn-primary" disabled={busy} onClick={() => go(askRing.mode, askRing.hours, true)}>{t('panels.loc.ringYes')}</button>
        <button className="btn-ghost" disabled={busy} onClick={() => go(askRing.mode, askRing.hours, false)}>{t('panels.loc.ringNo')}</button>
      </span>
    );
  }
  if (confirmRemove) {
    return (
      <span className="cn-loc" data-testid="panel-loc-remove-confirm">
        <span>{t('panels.removeConfirm')}</span>
        <button className="btn-danger" disabled={busy} onClick={() => go('remove')} data-testid="panel-loc-remove-yes">{t('panels.removeYes')}</button>
        <button className="btn-ghost" onClick={() => setConfirmRemove(false)}>{t('common.cancel')}</button>
      </span>
    );
  }
  return (
    <span className="cn-loc" data-testid="panel-loc-chooser">
      <strong>{t('panelCalNotice.locationChoose')}</strong>
      {modes.length === 1 && modes[0] === 'remove' && <span>{t('panelCalNotice.locationRemoveOnly')}</span>}
      {modes.includes('once') && (
        <button className="btn-primary" disabled={busy} onClick={() => setAskRing({ mode: 'once' })} data-testid="panel-loc-once">{t('panels.loc.allowOnce')}</button>
      )}
      {modes.includes('hours') && (
        <>
          <select value={hours} onChange={(e) => setHours(Number(e.target.value))} aria-label={t('panels.loc.hoursLabel')}>
            {HOURS.map((h) => (
              <option key={h} value={h}>{t(h === 4 ? 'panels.loc.hoursSuggested' : 'panels.loc.hoursN', { n: h })}</option>
            ))}
          </select>
          <button className="btn-primary" disabled={busy} onClick={() => setAskRing({ mode: 'hours', hours })} data-testid="panel-loc-hours">
            {t('panels.loc.allowHours', { n: hours })}
          </button>
        </>
      )}
      {modes.includes('permanent') && (
        <button className="btn-ghost" disabled={busy} onClick={() => go('permanent')} data-testid="panel-loc-permanent">{t('panels.loc.makeHome')}</button>
      )}
      {modes.includes('remove') && (
        <button className="btn-danger" disabled={busy} onClick={() => setConfirmRemove(true)} data-testid="panel-loc-remove">{t('panels.loc.notHomeRemove')}</button>
      )}
      <button className="btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
    </span>
  );
}

function openLabel(n: CalNotice): string {
  if (n.kind === 'confirm_key_added') return 'confirm.btn.my_phones';
  if (n.kind === 'phone_denied' || n.kind === 'settings_changed') return 'confirm.btn.open_panel';
  return n.kind === 'ask_family' ? 'panelCalNotice.openEdit' : n.eventId ? 'panelCalNotice.openEvent' : 'panelCalNotice.openCalendar';
}

/** AppShell 掛載（= 已登入）時開始輪詢，卸載時停 */
export function usePanelCalNoticePolling(): void {
  useEffect(() => panelCalNotice.start(), []);
}

export function PanelCalNotices() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const st = useSyncExternalStore(panelCalNotice.subscribe, panelCalNotice.get);
  const [expanded, setExpanded] = useState(false);
  const [choosing, setChoosing] = useState<string | null>(null);
  const list = visibleNotices(st);
  if (!list.length) return null;
  const shown = expanded ? list : list.slice(0, SHOW_MAX);
  const open = (n: CalNotice) => {
    if (n.server || isConfirmNotice(n.kind)) panelCalNotice.dismiss(n.id); // 點開 = 讀過（雲端標已讀，其他裝置也收起）
    navigate(noticeTarget(n));
  };
  return (
    <div className="cn-banners" data-testid="panel-cal-notices" aria-live="polite">
      {shown.map((n) => {
        const tx = noticeText(n);
        const busy = st.acking === n.id;
        const err = st.ackErr[n.id];
        const canAck = n.kind === 'unacked' && n.variant !== 'pending' && !!n.eventId && !!n.recurrenceId;
        const lbl = noticeAckLabel(n);
        const approveMsg = st.approveMsg[n.id];
        const approved = approveMsg === 'panelCalNotice.approved' || approveMsg === 'panelCalNotice.approveAlready' || approveMsg === 'panelCalNotice.locationRemoved';
        const isLoc = n.kind === 'location';
        return (
          <div key={n.id} className={`cn-banner cn-banner--${n.kind}`} role="status" data-testid={`panel-cal-notice-${n.kind}`} data-notice-id={n.id}>
            <span className="cn-banner-icon" aria-hidden="true">{isConfirmNotice(n.kind) ? '🔐' : isLoc ? '🌐' : n.kind === 'unacked' || n.kind === 'unacked_summary' ? '⏰' : '📅'}</span>
            <span className="cn-banner-text">
              <strong>{isLoc ? t(tx.key, tx.params) : n.panelName || (n.kind === 'confirm_key_added' ? t('confirm.notice_account') : '')}</strong>
              <span>{n.detail ?? noticeBodyFallback(n) ?? t(tx.key, tx.params)}</span>
              {err && <span className="error-text" role="alert">{t(err)}</span>}
              {approveMsg && (
                <span className={approved ? undefined : 'error-text'} role={approved ? 'status' : 'alert'} data-testid="panel-cal-notice-approve-msg">
                  {t(approveMsg)}
                </span>
              )}
            </span>
            <span className="cn-banner-actions">
              {isLoc && !approved && !!n.approveModes?.length && choosing !== n.id && (
                <button className="btn-primary" onClick={() => setChoosing(n.id)} data-testid="panel-loc-approve">{t('panelCalNotice.approveNetwork')}</button>
              )}
              {isLoc && choosing === n.id && !approved && <LocationChooser n={n} busy={st.approving === n.id} onClose={() => setChoosing(null)} />}
              {canAck && (
                <button className="btn-primary" disabled={busy} onClick={() => void panelCalNotice.ack(n)} data-testid="panel-cal-notice-ack">
                  {busy ? t('common.saving') : n.ackLabelText ?? t(lbl.key, lbl.params)}
                </button>
              )}
              {n.kind === 'unacked' && (
                <button className="btn-ghost" onClick={() => void panelCall.dial(n.panelId, n.panelName)} data-testid="panel-cal-notice-call">
                  {t('panelCalNotice.callPanel')}
                </button>
              )}
              {n.kind === 'unacked' && n.variant === 'pending' && n.approveMode && !approved && (
                <button className="btn-ghost" disabled={st.approving === n.id} onClick={() => void panelCalNotice.approve(n)} data-testid="panel-cal-notice-approve">
                  {st.approving === n.id ? t('common.saving') : t('panelCalNotice.approveNetwork')}
                </button>
              )}
              {n.kind === 'unacked' && n.variant === 'pending' && n.owner && !n.approveMode && (
                <button className="btn-ghost" onClick={() => navigate(`/devices/${encodeURIComponent(n.panelId)}`)} data-testid="panel-cal-notice-approve">
                  {t('panelCalNotice.approveNetwork')}
                </button>
              )}
              {isLoc ? (
                <button className="btn-ghost" onClick={() => navigate(`/devices/${encodeURIComponent(n.panelId)}`)} data-testid="panel-loc-networks">
                  {t('panelCalNotice.locationNetworks')}
                </button>
              ) : (
                <button className="btn-ghost" onClick={() => open(n)} data-testid="panel-cal-notice-open">
                  {t(openLabel(n))}
                </button>
              )}
              <button className="btn-ghost" aria-label={t('panelCalNotice.hide')} onClick={() => panelCalNotice.dismiss(n.id)} data-testid="panel-cal-notice-hide">
                {t('panelCalNotice.hide')}
              </button>
            </span>
          </div>
        );
      })}
      {list.length > SHOW_MAX && (
        <button className="btn-ghost cn-more" onClick={() => setExpanded(!expanded)} data-testid="panel-cal-notice-more">
          {expanded ? t('panelCalNotice.less') : t('panelCalNotice.more', { n: list.length - SHOW_MAX })}
        </button>
      )}
    </div>
  );
}
